import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSignupBootStore } from '../../../stores/signupBootStore';
import { useBootChoreographyStore } from '../../../stores/bootChoreographyStore';
import { BOOT_TIMING, BOOT_COPY } from './bootScene';
import { createFileLogger } from '../../../utils/logger';
import styles from './BootAnimation.module.scss';

const log = createFileLogger('BOOT_ANIMATION');

type BootStage = 'loading' | 'greeting' | 'mounting' | 'revealing';

/**
 * Retro, ready-gated boot sequence — mounted exactly once at the app root
 * (App.tsx) and otherwise renders nothing. `signupBootStore.active` is
 * flipped true by useFunnelRedirect.navigateToSpaceOrIntro BEFORE it
 * navigates away from the signup page, so this is already painted, full
 * screen, before the route change even starts — see signupBootStore.ts for
 * the full handoff this participates in.
 *
 * Unlike a fixed-timeline splash, the 'loading' stage loops indefinitely
 * (repeating bar fill + cycling status phrases) because there's no way to
 * know in advance how long the real signup + navigation + Space-mount work
 * will take. It only advances to 'greeting' once BOTH:
 *   - `spaceReady` — Space.tsx has mounted and reached the point it would
 *     otherwise reveal the desktop (see Space.tsx's handleIntroComplete).
 *   - `BOOT_TIMING.minLoadingHoldMs` has elapsed since activation.
 * ...so a fast signup never flashes the screen, and a slow one just holds
 * longer — the loop IS the loading indicator.
 *
 * After the greeting, this owns the ENTIRE desktop reveal, split into two
 * stages so the desktop is never uncovered while it's still hydrating:
 *
 *   - 'mounting': flips `signupBootStore.revealNow` (mounting the real
 *     DesktopShell underneath — see Space.tsx's `revealNow` effect) and
 *     `bootChoreographyStore` to 'pending' (which keeps every window surface
 *     hidden, see DesktopShell.tsx), but plays no reveal yet — the overlay
 *     just stays fully opaque while the desktop mounts and hydrates behind
 *     it. Advances to 'revealing' the moment `signupBootStore.desktopReady`
 *     goes true (reported by SpacePageWrapper.tsx once its own "everything
 *     is loaded" signal fires), bounded by `BOOT_TIMING.desktopReadyMaxWaitMs`
 *     as a backstop.
 *   - 'revealing': the overlay is hidden instantly (see .revealing in
 *     BootAnimation.module.scss — no wipe or slide, the desktop just
 *     appears), then immediately drives `bootChoreographyStore`'s stage to
 *     'bar' and, after a short pause, 'chat' — DesktopSystemBar fades its
 *     content in for 'bar', and DesktopShell's window surfaces + SpaceChat
 *     play their slide-ins once 'chat' hits — then resets everything once
 *     done.
 */
const BootAnimation: React.FC = () => {
  const active = useSignupBootStore((s) => s.active);
  const spaceReady = useSignupBootStore((s) => s.spaceReady);
  const desktopReady = useSignupBootStore((s) => s.desktopReady);

  const [stage, setStage] = useState<BootStage>('loading');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [minHoldElapsed, setMinHoldElapsed] = useState(false);
  const [reducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Fresh sequence every time the overlay (re)activates: reset local stage
  // state, start the minimum-hold timer, and (motion allowed) start cycling
  // status phrases. 'loading' otherwise has no fixed end — it holds until the
  // advance effect below sees both gates open.
  useEffect(() => {
    if (!active) return;
    log.log('[BootAnimation] activated');
    setStage('loading');
    setPhraseIndex(0);
    setMinHoldElapsed(false);

    const holdTimeout = window.setTimeout(() => setMinHoldElapsed(true), BOOT_TIMING.minLoadingHoldMs);
    const phraseInterval = reducedMotion ? null : window.setInterval(() => {
      setPhraseIndex((i) => (i + 1) % BOOT_COPY.statusPhrases.length);
    }, BOOT_TIMING.statusPhraseMs);

    return () => {
      clearTimeout(holdTimeout);
      if (phraseInterval) clearInterval(phraseInterval);
    };
  }, [active, reducedMotion]);

  // loading -> greeting: only once BOTH the real Space is ready to reveal AND
  // the minimum hold has elapsed. Order-independent — whichever resolves last
  // is what fires this.
  useEffect(() => {
    if (active && stage === 'loading' && spaceReady && minHoldElapsed) {
      setStage('greeting');
    }
  }, [active, stage, spaceReady, minHoldElapsed]);

  // greeting -> mounting, timed.
  useEffect(() => {
    if (stage !== 'greeting') return;
    const holdMs = reducedMotion ? BOOT_TIMING.reducedMotionHoldMs : BOOT_TIMING.greetingMs;
    const id = window.setTimeout(() => setStage('mounting'), holdMs);
    return () => clearTimeout(id);
  }, [stage, reducedMotion]);

  // 'mounting': mount the real desktop underneath, RIGHT NOW, in the same
  // synchronous tick as flipping the choreography to 'pending' — see the old
  // 'revealing' effect's comment below for why the ordering matters (Space.tsx
  // and SpaceChat both read these stores on their very first render). Unlike
  // the old single-stage version, no mask animation plays here: the overlay
  // just holds fully opaque while the desktop hydrates behind it. Bounded by
  // `desktopReadyMaxWaitMs` so a desktop that never reports ready (or a route
  // that skips SpacePageWrapper entirely) can't hang the reveal forever.
  useEffect(() => {
    if (stage !== 'mounting') return;
    log.log('[BootAnimation] mounting — desktop mounting underneath, mask staying shut');
    useBootChoreographyStore.getState().setStage('pending');
    useSignupBootStore.getState().setRevealNow(true);

    if (reducedMotion) {
      // No CSS mask animation plays either way — advance straight through
      // once the desktop has had a chance to mount.
      setStage('revealing');
      return undefined;
    }

    const backstopId = window.setTimeout(() => {
      log.warn('[BootAnimation] desktopReady backstop fired — revealing anyway');
      setStage('revealing');
    }, BOOT_TIMING.desktopReadyMaxWaitMs);
    return () => clearTimeout(backstopId);
  }, [stage, reducedMotion]);

  // mounting -> revealing, the moment the desktop itself reports ready (see
  // signupBootStore.setDesktopReady, driven by SpacePageWrapper's isUIReady
  // signal). The double rAF lets the browser actually paint the now-mounted,
  // now-ready desktop before the overlay is hidden and it becomes visible.
  useEffect(() => {
    if (stage !== 'mounting' || reducedMotion || !desktopReady) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        log.log('[BootAnimation] desktop reported ready — revealing it');
        setStage('revealing');
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [stage, reducedMotion, desktopReady]);

  // Timer scheduled from within the 'revealing' effect below — tracked in a
  // ref (rather than effect-local) so a re-entrant advanceToChat call can
  // clear any pending one from a previous pass.
  const revealTimeoutsRef = useRef<number[]>([]);
  // Guards advanceToChat so it only ever runs once per 'revealing' pass.
  const chatAdvancedRef = useRef(false);

  // Holds on the bare, chrome-less desktop for `postRevealPauseMs` before
  // flipping to 'chat' — otherwise the dock/sidebar/window/chat slide-in
  // would start in the exact same tick the desktop is revealed, so the
  // reveal never reads as its own completed beat.
  const advanceToChat = useCallback(() => {
    if (chatAdvancedRef.current) return;
    chatAdvancedRef.current = true;
    log.log('[BootAnimation] desktop revealed — pausing before bringing in chat');
    const pauseId = window.setTimeout(() => {
      log.log('[BootAnimation] pause done — bringing in chat');
      useBootChoreographyStore.getState().setStage('chat');
      const idleId = window.setTimeout(() => {
        log.log('[BootAnimation] sequence finished, resetting');
        useBootChoreographyStore.getState().setStage('idle');
        useSignupBootStore.getState().reset();
      }, BOOT_TIMING.chatEntranceDelayMs + BOOT_TIMING.chatEntranceMs);
      revealTimeoutsRef.current.push(idleId);
    }, BOOT_TIMING.postRevealPauseMs);
    revealTimeoutsRef.current.push(pauseId);
  }, []);

  // 'revealing': the desktop is already mounted and ready (done by the
  // 'mounting' stage above), and the overlay hides itself instantly (see
  // .revealing in BootAnimation.module.scss — no wipe or slide). This stage
  // just drives the choreography signals straight through: 'bar' first (so
  // DesktopSystemBar can fade its content in), then, after a short pause,
  // 'chat' (dock/window/chat slide-ins) via advanceToChat above.
  useEffect(() => {
    if (stage !== 'revealing') return;
    log.log('[BootAnimation] revealing — desktop shown instantly');
    revealTimeoutsRef.current.forEach(clearTimeout);
    revealTimeoutsRef.current = [];
    chatAdvancedRef.current = false;

    useBootChoreographyStore.getState().setStage('bar');
    advanceToChat();
    return () => revealTimeoutsRef.current.forEach(clearTimeout);
  }, [stage, advanceToChat]);

  if (!active) return null;

  const revealClass = reducedMotion ? styles.revealStatic : styles.reveal;

  return (
    <div
      className={`${styles.overlay} ${stage === 'revealing' ? styles.revealing : ''}`}
      role="presentation"
    >
      <div className={styles.crtGrain} />
      <div className={styles.crtVignette} />

      {stage === 'loading' && (
        <div className={revealClass}>
          {!reducedMotion && <div className={styles.scanline} />}
          <div className={styles.content}>
            <div className={styles.loaderBar}>
              <span className={styles.loaderBracket}>[</span>
              <div className={styles.loaderBarTrack}>
                <div className={styles.loaderBarFill} />
              </div>
              <span className={styles.loaderBracket}>]</span>
            </div>
            <div className={styles.statusText}>{BOOT_COPY.statusPhrases[phraseIndex]}</div>
          </div>
        </div>
      )}

      {stage === 'greeting' && (
        <div className={revealClass}>
          {!reducedMotion && <div className={styles.scanline} />}
          <div className={styles.content}>
            <div className={styles.logo} role="img" aria-label="River" />
            <div className={styles.greeting}>{BOOT_COPY.greeting}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BootAnimation;
